import React, { memo, useState, useCallback, useRef, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from '@mui/material';
import Webcam from 'react-webcam';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { heicTo } from 'heic-to/csp';

import Button from '../Button';
import FaceValidationOverlay from './FaceValidationOverlay';
import { ReactComponent as CloseIcon } from 'assets/icons/ClearWhite.svg';
import { ReactComponent as CaptureIcon } from 'assets/icons/Capture.svg';
import { ReactComponent as GalleryIcon } from 'assets/icons/Gallery.svg';
import { ReactComponent as FlipCameraIcon } from 'assets/icons/FlipCamera.svg';
import { ReactComponent as CameraErrorImage } from 'assets/images/CameraError.svg';

import { FaceValidationStatus } from './types';
import type { FaceValidationResult } from './types';
import { useStyles } from './styles';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_IMAGE_FORMATS = {
  mimeTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heics', 'image/heif'],
  extensions: ['.jpg', '.jpeg', '.png', '.heic', '.heics', '.heif'],
};
const PDF_FORMAT = { mimeType: 'application/pdf', extension: '.pdf' };

export interface ICapturePhotoProps {
  enableFacialRecognition?: boolean;
  isProceedLoading?: boolean;
  modalStyleClass?: string;
  multiple?: boolean;
  onClose: () => void;
  onCapture?: (imageSrc: string) => void;
  onProceed: (imageSrc: string | string[]) => void;
  isPdfEnabled?: boolean;
}

const DEFAULT_FACE_VALIDATION_RESULTS: FaceValidationResult = {
  faceCentered: FaceValidationStatus.FAIL,
  headStraight: FaceValidationStatus.PASS,
  goodLighting: FaceValidationStatus.PENDING,
  clearNoBlur: FaceValidationStatus.PENDING,
  eyesVisible: FaceValidationStatus.PASS,
  faceClearlyVisible: FaceValidationStatus.PENDING,
  singleFaceDetected: FaceValidationStatus.PENDING,
  correctDistance: FaceValidationStatus.FAIL,
};

const CapturePhoto = ({
  enableFacialRecognition = false,
  isProceedLoading,
  modalStyleClass = '',
  multiple = false,
  onClose,
  onCapture,
  onProceed,
  isPdfEnabled = false,
}: ICapturePhotoProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasMediaError, setHasMediaError] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const ovalBorderClass = useMemo(() => {
    const results = DEFAULT_FACE_VALIDATION_RESULTS;
    const values = Object.values(results);
    const hasAnyFail = values.some(v => v === FaceValidationStatus.FAIL);
    const allPass = values.every(v => v === FaceValidationStatus.PASS);

    if (hasAnyFail) return classes.faceOvalGuideFail;
    if (allPass) return classes.faceOvalGuidePass;
    return classes.faceOvalGuidePending;
  }, [classes]);

  const checkAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    } catch (error) {
      setHasMultipleCameras(false);
    }
  }, []);

  // Per modal: PDFs are allowed only where the caller enables them
  const allowedFormats = useMemo(
    () =>
      isPdfEnabled
        ? {
            mimeTypes: [...ALLOWED_IMAGE_FORMATS.mimeTypes, PDF_FORMAT.mimeType],
            extensions: [...ALLOWED_IMAGE_FORMATS.extensions, PDF_FORMAT.extension],
          }
        : ALLOWED_IMAGE_FORMATS,
    [isPdfEnabled],
  );

  const onFlipCamera = useCallback(() => {
    setFacingMode(prevState => (prevState === 'user' ? 'environment' : 'user'));
  }, []);

  const onUserMediaError = useCallback(err => {
    setHasMediaError(true);
  }, []);

  const onUserMedia = useCallback(() => {
    checkAvailableCameras();
  }, [checkAvailableCameras]);

  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    onCapture?.(imageSrc);
  }, [onCapture]);

  const isValidFileType = useCallback(
    (file: File) => {
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return allowedFormats.mimeTypes.includes(file.type) || allowedFormats.extensions.includes(fileExtension);
    },
    [allowedFormats],
  );

  const showMultipleFileErrors = useCallback(
    (validCount: number, rejectedCount: number, invalidTypeCount: number, oversizedCount: number) => {
      if (rejectedCount === 0) return;

      const isAllRejected = rejectedCount > 0 && validCount === 0;
      const variant = isAllRejected ? 'error' : 'warning';

      let errorMessage = '';

      if (invalidTypeCount > 0 && oversizedCount > 0) {
        errorMessage = isAllRejected
          ? t('capturePhoto.errors.allFilesRejectedMixed', { invalidTypeCount, oversizedCount })
          : t('capturePhoto.errors.multipleFilesWithInvalidTypes', { validCount, invalidTypeCount, oversizedCount });
      } else if (invalidTypeCount > 0) {
        errorMessage = isAllRejected
          ? t('capturePhoto.errors.invalidFileType')
          : t('capturePhoto.errors.multipleFilesInvalidTypeOnly', { validCount, invalidTypeCount });
      } else {
        errorMessage = isAllRejected
          ? t('capturePhoto.errors.fileSizeExceeded')
          : t('capturePhoto.errors.multipleFilesSelected', { validCount, rejectedCount });
      }

      enqueueSnackbar(errorMessage, { variant });
    },
    [t],
  );

  // Fallback 1: <img> element — uses the browser's native image pipeline (works in Safari for all HEIC)
  const convertHeicViaImg = useCallback(
    (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          } catch (e) {
            URL.revokeObjectURL(url);
            reject(e);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('img element could not decode HEIC'));
        };
        img.src = url;
      }),
    [],
  );

  // Fallback 2: createImageBitmap — works in Chrome 118+ when the OS HEVC codec is available
  const convertHeicViaBitmap = useCallback(async (file: File): Promise<string> => {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', 0.8);
  }, []);

  const convertFileToBase64 = useCallback(async (file: File): Promise<string> => {
    const isHeic = /\.(heic|heif|heics)$/i.test(file.name) || /image\/(heic|heif)/i.test(file.type);

    if (isHeic) {
      // Primary: heic-to (modern WASM libheif — decodes 10-bit/HDR/tiled HEIC, browser-agnostic)
      try {
        const convertedBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.8 });
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(convertedBlob);
        });
      } catch (e1) {
        console.error('heic-to failed:', e1);
      }

      // Try native <img> element (works in Safari for all HEIC variants)
      try {
        return await convertHeicViaImg(file);
      } catch (e2) {
        console.error('img fallback failed:', e2);
      }

      // Try createImageBitmap (works in Chrome when OS HEVC codec is available)
      try {
        return await convertHeicViaBitmap(file);
      } catch (e3) {
        console.error('createImageBitmap fallback failed:', e3);
      }

      // All decoders exhausted — propagate so the caller can show a proper error
      throw new Error('HEIC format not supported in this browser');
    } else {
      // For non-HEIC files, just read as base64
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  }, [convertHeicViaImg, convertHeicViaBitmap]);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      setIsConverting(true);

      if (multiple) {
        // Handle multiple image selection
        const filesArray = Array.from(files);
        let rejectedCount = 0;
        let invalidTypeCount = 0;
        let oversizedCount = 0;

        // Process all files in parallel
        const filePromises = filesArray.map(async file => {
          if (!isValidFileType(file)) {
            rejectedCount++;
            invalidTypeCount++;
            return null;
          } else if (file.size > MAX_FILE_SIZE) {
            rejectedCount++;
            oversizedCount++;
            return null;
          } else {
            try {
              return await convertFileToBase64(file);
            } catch (error) {
              rejectedCount++;
              return null;
            }
          }
        });

        const results = await Promise.all(filePromises);
        const validFiles = results.filter((result): result is string => result !== null);

        showMultipleFileErrors(validFiles.length, rejectedCount, invalidTypeCount, oversizedCount);

        if (validFiles.length > 0) {
          onProceed(validFiles);
        }
        setIsConverting(false);
        onClose();
      } else {
        // Handle single image selection
        const file = files[0];

        if (!isValidFileType(file)) {
          setIsConverting(false);
          enqueueSnackbar(t('capturePhoto.errors.invalidFileType'), {
            variant: 'error',
          });
          return;
        }

        if (file.size > MAX_FILE_SIZE) {
          setIsConverting(false);
          enqueueSnackbar(t('capturePhoto.errors.fileSizeExceeded'), {
            variant: 'error',
          });
          return;
        }

        try {
          const convertedImage = await convertFileToBase64(file);
          setIsConverting(false);
          onClose();
          onProceed(convertedImage);
        } catch (error) {
          setIsConverting(false);
          enqueueSnackbar(t('capturePhoto.errors.conversionFailed'), {
            variant: 'error',
          });
        }
      }

      // Reset the input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [multiple, isValidFileType, onProceed, onClose, t, showMultipleFileErrors, convertFileToBase64],
  );

  const pickFromGallery = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onRetake = useCallback(() => {
    setCapturedImage(null);
    onCapture?.(null);
  }, [onCapture]);

  const onSubmit = useCallback(() => {
    onProceed?.(capturedImage);
  }, [capturedImage, onProceed]);

  return (
    <Dialog
      open={true}
      data-testid="capture-photo-modal"
      className={`${classes.modal} ${modalStyleClass}`}
      onClose={!isProceedLoading && onClose}
    >
      <DialogTitle className={classes.modalTitle}>
        {t('capturePhoto.title')}
        <IconButton
          aria-label="close"
          disabled={isProceedLoading}
          data-testid="capture-photo-modal-close-button"
          className={classes.closeIconParent}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={`${classes.modalContent} ${hasMediaError ? classes.modalContentError : ''}`}>
        {hasMediaError ? (
          <>
            <CameraErrorImage />
            <p className={classes.noAccessText}>{t('capturePhoto.errors.noAccessToCamera')}</p>
            <p className={classes.errorReasonsText}>{t('capturePhoto.errorReasons')}</p>
            <ul className={classes.errorList}>
              <li className={classes.errorText}>{t('capturePhoto.errors.noWebcam')}</li>
              <li className={classes.errorText}>{t('capturePhoto.errors.webcamDisabled')}</li>
              <li className={classes.errorText}>{t('capturePhoto.errors.webcamOff')}</li>
            </ul>
          </>
        ) : capturedImage ? (
          <>
            <img alt="Captured" srcSet={`${capturedImage} 1x, ${capturedImage} 2x`} className={classes.capturedImage} />
            <div className={`${classes.modalActionsParent} ${classes.buttonsParent}`}>
              <Button
                label={t('capturePhoto.buttons.retake')}
                variant={'outlined'}
                styleClass={classes.retakeButton}
                disabled={isProceedLoading}
                testId="capture-photo-retake-button"
                onClick={onRetake}
              />
              <Button
                label={t('capturePhoto.buttons.proceed')}
                isLoading={isProceedLoading}
                styleClass={classes.proceedButton}
                testId="capture-photo-proceed-button"
                onClick={onSubmit}
              />
            </div>
          </>
        ) : (
          <>
            {!isConverting && (
              <Webcam
                ref={webcamRef}
                audio={false}
                width={'100%'}
                height={'100%'}
                screenshotFormat="image/png"
                screenshotQuality={1}
                mirrored={facingMode === 'user'}
                disablePictureInPicture={true}
                videoConstraints={{
                  facingMode,
                }}
                className={classes.webcam}
                onUserMedia={onUserMedia}
                onUserMediaError={onUserMediaError}
              />
            )}
            {enableFacialRecognition && !isConverting && (
              <FaceValidationOverlay validationResults={DEFAULT_FACE_VALIDATION_RESULTS} />
            )}
            {enableFacialRecognition && !isConverting && (
              <div className={`${classes.faceOvalGuide} ${ovalBorderClass}`} />
            )}
            <div className={classes.modalActionsParent}>
              <IconButton
                className={classes.iconButton}
                onClick={pickFromGallery}
                disabled={isConverting}
                data-testid="capture-photo-gallery-button"
              >
                <GalleryIcon />
              </IconButton>
              <IconButton
                className={classes.iconButton}
                onClick={capturePhoto}
                disabled={isConverting}
                data-testid="capture-photo-capture-button"
              >
                <CaptureIcon />
              </IconButton>
              <IconButton
                className={classes.iconButton}
                onClick={onFlipCamera}
                disabled={isConverting || !hasMultipleCameras}
                data-testid="capture-photo-flip-button"
              >
                <FlipCameraIcon />
              </IconButton>
            </div>
            {isConverting && (
              <div className={classes.loadingContainer}>
                <CircularProgress className={classes.activityIndicator} size={48} />
                <p className={classes.convertingText}>{t('capturePhoto.convertingText')}</p>
              </div>
            )}
          </>
        )}

        {/* Hidden File Input for Gallery */}
        <input
          ref={fileInputRef}
          type="file"
          accept={[...allowedFormats.mimeTypes, ...allowedFormats.extensions].join(',')}
          multiple={multiple}
          data-testid="capture-photo-file-input"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default memo(CapturePhoto);
